import { DollarSign, MapPin, Calendar } from 'lucide-react';

export default function Projects() {
  const projects = [
    {
      title: 'Marina Bay Mixed-Use Development',
      value: '$45,000,000',
      location: 'San Francisco, CA',
      year: '2022-2023',
      type: 'Mixed-Use',
      description: 'A landmark waterfront development featuring luxury residential units, premium retail space, and community amenities. This project showcased our ability to navigate complex zoning requirements and coordinate multiple stakeholders.',
      image: 'https://images.unsplash.com/photo-1738627209964-764a44aeee9e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBza3lzY3JhcGVyJTIwc3Vuc2V0fGVufDB8fHx8MTc2NTE1NDQyNnww&ixlib=rb-4.1.0&q=85',
      highlights: [
        'Successfully linked client with three institutional investors',
        'Completed 6 months ahead of schedule',
        '98% occupancy within 3 months of completion'
      ]
    },
    {
      title: 'Downtown Tower Redevelopment',
      value: '$62,000,000',
      location: 'Los Angeles, CA',
      year: '2021-2023',
      type: 'Commercial',
      description: 'Complete renovation and repositioning of a historic downtown office tower. Our team coordinated complex financing, historic preservation requirements, and modern amenity upgrades.',
      image: 'https://images.unsplash.com/photo-1764603852093-6e4918c3197a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBza3lzY3JhcGVyJTIwc3Vuc2V0fGVufDB8fHx8MTc2NTE1NDQyNnww&ixlib=rb-4.1.0&q=85',
      highlights: [
        'Structured multi-layer financing with five capital sources',
        'Preserved historic architectural features',
        'Achieved LEED Gold certification'
      ]
    },
    {
      title: 'Beverly Hills Luxury Residential',
      value: '$28,000,000',
      location: 'Beverly Hills, CA',
      year: '2020-2022',
      type: 'Residential',
      description: 'An exclusive residential development featuring ultra-luxury homes with premium finishes and smart home technology. Project required careful market positioning and high-net-worth buyer cultivation.',
      image: 'https://images.unsplash.com/photo-1751698186759-5bac83376c9f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBza3lzY3JhcGVyJTIwc3Vuc2V0fGVufDB8fHx8MTc2NTE1NDQyNnww&ixlib=rb-4.1.0&q=85',
      highlights: [
        'All units sold during pre-construction phase',
        'Premium pricing achieved above market average',
        'Connected developer with high-net-worth international buyers'
      ]
    },
    {
      title: 'Silicon Valley Office Campus',
      value: '$38,000,000',
      location: 'Palo Alto, CA',
      year: '2019-2021',
      type: 'Commercial',
      description: 'Modern office campus designed for tech companies, featuring collaborative workspaces, outdoor areas, and sustainable design elements. Complex financing structure required creative investor partnerships.',
      image: 'https://images.unsplash.com/photo-1557485670-c2c2215bf386?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxkaXZlcnNlJTIwYnVzaW5lc3MlMjBtZWV0aW5nJTIwcmVhbCUyMGVzdGF0ZSUyMGludmVzdG1lbnQlMjBjaGFydHMlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzY1MTU0NDk5fDA&ixlib=rb-4.1.0&q=85',
      highlights: [
        'Secured Fortune 500 anchor tenant pre-construction',
        'Innovative funding structure with venture capital participation',
        'LEED Platinum certified'
      ]
    }
  ];

  const caseStudy = {
    title: 'Case Study: Complex Multi-Source Funding Success',
    challenge: 'A developer approached us with an ambitious $62M mixed-use project that required multiple funding sources due to the project\'s complexity and scale. Traditional lenders were hesitant to fully finance the unconventional design and mixed revenue streams.',
    solution: 'Leveraging our extensive investor network and 30+ years of experience, we structured a sophisticated capital stack combining traditional construction financing, mezzanine debt, and equity from three strategic investors we connected the client with. Our team coordinated due diligence across all parties and negotiated terms that protected the developer\'s interests while satisfying investor requirements.',
    result: 'The project was successfully funded and completed on schedule. The developer maintained majority ownership while accessing the capital needed to bring their vision to life. Two of the connected investors have since partnered with the developer on additional projects.'
  };

  return (
    <div className="min-h-screen pt-20" data-testid="projects-page">
      <section className="py-24 md:py-32 bg-[#020617]" data-testid="projects-hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h1 className="text-5xl md:text-7xl font-medium tracking-tight leading-none text-[#f8fafc] mb-8" data-testid="projects-title">
            Our <span className="text-[#d4af37]">Projects</span>
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-slate-300 max-w-3xl mx-auto mb-8">
            A portfolio of landmark developments representing over $150 million in successful project delivery
          </p>
          <div className="inline-flex items-center space-x-3 glass-morphism px-8 py-4">
            <DollarSign className="text-[#d4af37]" size={32} />
            <div className="text-left">
              <div className="text-3xl font-medium text-[#d4af37]">$150M+</div>
              <div className="text-sm text-slate-400">Portfolio Value</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#0f172a]" data-testid="projects-gallery-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="space-y-24">
            {projects.map((project, index) => (
              <div
                key={index}
                data-testid={`project-${index}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-dense' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="relative aspect-[4/3] overflow-hidden group">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="text-sm tracking-widest uppercase text-[#d4af37] mb-3">
                    {project.type}
                  </div>
                  <h2 className="text-3xl md:text-4xl font-normal text-[#f8fafc] mb-6">
                    {project.title}
                  </h2>
                  
                  <div className="flex flex-wrap gap-6 mb-6">
                    <div className="flex items-center space-x-2">
                      <DollarSign className="text-[#d4af37]" size={20} />
                      <span className="text-slate-300 text-lg font-medium">{project.value}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <MapPin className="text-[#d4af37]" size={20} />
                      <span className="text-slate-300">{project.location}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="text-[#d4af37]" size={20} />
                      <span className="text-slate-300">{project.year}</span>
                    </div>
                  </div>
                  
                  <p className="text-base leading-relaxed text-slate-400 mb-6">
                    {project.description}
                  </p>
                  
                  <div className="space-y-3">
                    <p className="text-sm tracking-widest uppercase text-[#d4af37]">Key Highlights</p>
                    {project.highlights.map((highlight, hIndex) => (
                      <div key={hIndex} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-[#d4af37] rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-base text-slate-300">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#020617]" data-testid="case-study-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="glass-morphism p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-normal text-[#d4af37] mb-12 text-center">
              {caseStudy.title}
            </h2>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-medium text-[#f8fafc] mb-4">The Challenge</h3>
                <p className="text-base leading-relaxed text-slate-300">
                  {caseStudy.challenge}
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-medium text-[#f8fafc] mb-4">Our Solution</h3>
                <p className="text-base leading-relaxed text-slate-300">
                  {caseStudy.solution}
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-medium text-[#f8fafc] mb-4">The Result</h3>
                <p className="text-base leading-relaxed text-slate-300">
                  {caseStudy.result}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}